import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Error from 'core/errors/Error';
import Response from 'app/config/Response';

const MenuItem = (menus = []) =>
  menus.flatMap((item) => [item, ...(item.children ? MenuItem(item.children) : [])]);

const normalizePermissionInput = (permission) => {
  if (!permission) return [];
  if (Array.isArray(permission)) {
    return permission.filter(Boolean).map((item) => String(item).trim().toUpperCase());
  }
  return [String(permission).trim().toUpperCase()].filter(Boolean);
};

const isItemSelected = (value) =>
  !(
    value === false ||
    value === 0 ||
    value === '0' ||
    (typeof value === 'string' && value.toLowerCase() === 'false')
  );

const getPermissionNamesFromMenus = (menus = []) => {
  return new Set(
    MenuItem(menus)
      .filter((item) => isItemSelected(item?.isSelected))
      .map((item) => (item?.name ? String(item.name).trim().toUpperCase() : null))
      .filter(Boolean),
  );
};

const hasPermissionAccess = (userPermissionNames, requiredPermissions = []) => {
  const normalizedPermissions = normalizePermissionInput(requiredPermissions);
  if (!normalizedPermissions.length) return true;
  return normalizedPermissions.some((permission) => userPermissionNames.has(permission));
};

const isPathMatch = (pathname = '', basePath = '') =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

const withRole = () => (WrappedComponent) => (props) => {
  const { pathname, state } = useLocation();

  const { loading, success, error, response } = useSelector(
    (state) => state.rolesAndPermissions.userRolesAndPermissions,
  );

  if (loading || (!success && !error)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
        <CircularProgress />
      </Box>
    );
  }

  const menus = MenuItem(response?.data || []);

  const menuPaths = menus
    .map((m) => m.url)
    .filter((url) => typeof url === 'string' && url.startsWith('/'));

  const isPathAllowed = menuPaths.some((path) => isPathMatch(pathname, path));

  const fromPath = typeof state?.fromPath === 'string' ? state.fromPath : '';
  const isNavigatedFromAllowedPath = fromPath
    ? menuPaths.some((path) => isPathMatch(fromPath, path))
    : false;

  if (isPathAllowed || isNavigatedFromAllowedPath) {
    return <WrappedComponent {...props} />;
  }

  return <Error status={Response.HTTP_FORBIDDEN} />;
};

export const withPermission =
  ({ permission } = {}) =>
  (WrappedComponent) =>
  (props) => {
    const { loading, success, error, response } = useSelector(
      (state) => state.rolesAndPermissions.userRolesAndPermissions,
    );

    if (loading || (!success && !error)) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
          <CircularProgress />
        </Box>
      );
    }

    const userPermissionNames = getPermissionNamesFromMenus(response?.data || []);
    const isAllowed = hasPermissionAccess(userPermissionNames, permission);

    if (isAllowed) {
      return <WrappedComponent {...props} />;
    }

    return <Error status={Response.HTTP_FORBIDDEN} />;
  };

export const HasPermission = ({ permission, fallback = null, children }) => {
  const { loading, success, error, response } = useSelector(
    (state) => state.rolesAndPermissions.userRolesAndPermissions,
  );

  if (loading || (!success && !error)) {
    return fallback;
  }

  const userPermissionNames = getPermissionNamesFromMenus(response?.data || []);
  const isAllowed = hasPermissionAccess(userPermissionNames, permission);
  return isAllowed ? children : fallback;
};

export const hasUserPermission = (permission) => {
  const { loading, success, error, response } = useSelector(
    (state) => state.rolesAndPermissions.userRolesAndPermissions,
  );

  if (loading || (!success && !error)) {
    return false;
  }

  const userPermissionNames = getPermissionNamesFromMenus(response?.data || []);
  return hasPermissionAccess(userPermissionNames, permission);
};

export default withRole;
